import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';

import { debounceTime, distinctUntilChanged, filter, fromEvent, map, of, pairwise, Subject, Subscription, switchMap, takeUntil, tap } from 'rxjs';

import { ParameterTypeEnum } from '@commons/enums/parameter-type.enum';
import { Vehicle } from '@commons/models/domain/Vehicle';
import { VehicleService } from '@commons/services/vehicle/vehicle.service';
import { PermissionService } from '@shared/services/permission.service';
import { VehicleListFilterRequest } from '@commons/models/remote.request/VehicleListFilterRequest';
import { IFilterVehicle, IFilterVehicleOption, ISubmitFilters } from '@src/app/interfaces/filters-vehicle.interface';
import { OptionItem } from '@commons/models/other/option-item.model';
import { TranslateHelperService } from '@shared/services/translate-helper.service';
import { Consts } from '@commons/consts/consts';
import { TranslateService } from '@ngx-translate/core';
import { FilterLockType, FilterVehicleType } from '@src/app/enum/filters-vehicle.enum';
import { TagsListFilterRequest } from '@commons/models/remote.request/TagsListFilterRequest';
import { Tag } from '@commons/models/domain/Tag';
import { WebAccessService } from '@commons/services/web.access/web-access.service';
import { get, isNumber } from 'lodash';
import { findVehicleSpeedParamValue, isVehicleBlocked } from '@src/app/utils/params-binary-vehicle';
import { DriverFullNamePipe } from '@commons/pipes/driver-full-name.pipe';
import { LOCAL_STORAGE_VAR } from '@src/app/utils/localstorage';
import { MapService } from '@commons/services/map/map/map.service';
import { NavigationEnd, Router } from '@angular/router';
import { calculateVehicleTimeInCurrentState, getVehicleCoords } from '@src/app/utils/vehicle-fncs';
import { NewNavigationService } from '@shared/services/new-navigation.service';
import { VehicleFullColumnId } from '@src/app/enum/vehicle-full.enum';
import { IVehicleFullColumnSort } from '@src/app/interfaces/vehicle.interface';
import { getConfigSavedListSortColumns } from '@src/app/utils/vehicle-list-fncs';
import { LanguageService } from '@shared/services/language.service';

const ANIMATION_TOGGLE_LIST_TIME = 150;
const TIME_OUT_FOR_LOADING_TRANSLATES = 1000;

@Component({
  selector: 'app-list-vehicle-bottom',
  templateUrl: './list-vehicle-bottom.component.html',
  styleUrls: ['./list-vehicle-bottom.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [DriverFullNamePipe],
})
export class ListVehicleBottomComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('resize', { static: true }) resizeElement!: ElementRef;
  @ViewChild('listWrapper', { static: true }) listWrapper!: ElementRef;
  @ViewChild('listVehicleBottom', { static: true }) containerRef!: ElementRef;

  private mouseMoveSubscription!: Subscription;
  private componentSubsc: Subscription = new Subscription();
  private onChangeVehicleFollowSubsc: Subscription;
  private initialY: number = 0;
  private onChangeingPanelHeight: boolean;

  public sortField: string = '';
  public sortOrder: number = 1;
  public isVisibleList: boolean;
  public isVisibleConfigColumnPanel: boolean;


  public form: FormGroup;
  public cars: any[] = [];
  public cols: any[] = [];
  public scrollHeight: string = this.getVehicleListHeightLocalStorage();
  public hasBlockerReadPermissionUser: boolean;

  public vehicles: Vehicle[] = [];

  public onChangePanelHeight$: Subject<void> = new Subject();
  public isVisibleLoader: boolean;
  public readonly PARAMETER_TYPE = ParameterTypeEnum;
  public readonly filteredStatuses = [
    { filterType: 0, statuses: [] },
    { filterType: 1, statuses: [3, 4, 5] },
    { filterType: 2, statuses: [1] },
    { filterType: 4, statuses: [2] },
    { filterType: 6, statuses: [6] }
  ];
  public readonly columnsWidth: any = {
    controls: 'min-width: 170px; max-width: 170px;',
    group: 'min-width: 200px; max-width: 200px;',
    vehicleName: 'min-width: 200px; max-width: 200px;',
    deviceID: 'min-width: 160px; max-width: 160px;',
    sentGeoID: 'min-width: 160px; max-width: 160px;',
    address: 'min-width: 220px; max-width: 220px;',
    driver: 'min-width: 120px; max-width: 120px;',
    speed: 'min-width: 100px; max-width: 100px;',
    lock: 'min-width: 120px; max-width: 120px;',
    time: 'min-width: 80px; max-width: 80px;',
    rpm: 'min-width: 90px; max-width: 90px;',
    etoll: 'min-width: 90px; max-width: 90px;',
    sent: 'min-width: 90px; max-width: 90px;',
    tacho: 'min-width: 150px; max-width: 150px; text-align: center;',
    networkType: 'min-width: 85px; max-width: 85px;',
    fuel: 'min-width: 120px; max-width: 120px;',
  };
  public sortableColumns: any = {
    vehicleName: 'vehicleName',
    St: 'St',
    speed: 'speed',
    driver: 'driver',
    etoll: 'etoll',
    gps: 'gps',
    gsm: 'gsm',
    fuel: 'fuel',
    rpm: 'rpm',
    deviceID: 'deviceID',
    sentGeoID: 'sentGeoID',
    lock: 'lock',
    networkType: 'networkType',
    time: 'time',
    sent: 'sent',
  };

  // filters
  public filtersStatusConfig: IFilterVehicle;
  public vehicleStatuses: OptionItem[] = [];
  public filtersStatusCurrentFilters: ISubmitFilters;
  public isVisibleLockFilter: boolean;

  public filtersLockConfig: IFilterVehicle;
  public filtersGroupConfig: IFilterVehicle;
  public filtersGroupCurrentFilters: ISubmitFilters;
  public filtersLockCurrentFilters: ISubmitFilters;

  // sort columns
  public configListSortColumns: IVehicleFullColumnSort[] = getConfigSavedListSortColumns() || [];

  constructor(
    private fb: FormBuilder,
    private vehicleService: VehicleService,
    private permissionService: PermissionService,
    private translateHelperService: TranslateHelperService,
    private translate: TranslateService,
    private webAccess: WebAccessService,
    private driverFullNamePipe: DriverFullNamePipe,
    private renderer2: Renderer2,
    private mapService: MapService,
    private router: Router,
    public newNavigationService: NewNavigationService,
    private languageService: LanguageService,
  ) {
    this.form = this.fb.group({
      Field: ['Description'],
      Status: null,
      Statuses: [],
      Phrase: [null],
      Tags: [[]],
      IgnitionBlockerState: [null],
    });
    this.setDefaultConfigColumnsList();
    this.checkIfUserCanVisibleBlockerInfo();
  }

  ngOnInit(): void {
    this.onChangeRoutePage();
    this.onChangeVisibleList();
    this.onChangeFiltersFormValue();
    this.onChangePanelHeight();
    this.setTranslations();
  }

  ngAfterViewInit(): void {
    this.resizeInit();
    this.setInitContainerHeight();
  }

  ngOnDestroy(): void {
    this.mouseMoveSubscription?.unsubscribe();
    this.componentSubsc?.unsubscribe();
    this.onChangeVehicleFollowSubsc?.unsubscribe();
  }

  private setDefaultConfigColumnsList(): void {
    setTimeout(() => {
      this.cols = [
        { field: 'vehicleName', header: this.translate.instant('labels.name'), id: VehicleFullColumnId.name },
        { field: 'group', header: this.translate.instant('vehicle-indicator.group'), id: VehicleFullColumnId.group },
        { field: 'St', header: this.translate.instant('labels.status'), id: VehicleFullColumnId.status },
        { field: 'time', header: this.translate.instant('labels.time'), id: VehicleFullColumnId.time },
        { field: 'speed', header: this.translate.instant('labels.speed'), id: VehicleFullColumnId.speed },
        { field: 'address', header: this.translate.instant('labels.address'), id: VehicleFullColumnId.adress  },
        { field: 'driver', header: this.translate.instant('labels.driver'), id: VehicleFullColumnId.driver },
        { field: 'gps', header: this.translate.instant('labels.gps'), id: VehicleFullColumnId.gps },
        { field: 'gsm', header: this.translate.instant('labels.gsm'), id: VehicleFullColumnId.gsm },
        { field: 'fuel', header: this.translate.instant('labels.fuel'), id: VehicleFullColumnId.fuel },
        { field: 'rpm', header: this.translate.instant('vehicle-indicator.rpm'), id: VehicleFullColumnId.rpm },
        { field: 'controls', header: this.translate.instant('sensors'), id: VehicleFullColumnId.sensor },
        { field: 'tacho', header: this.translate.instant('labels.tacho'), id: VehicleFullColumnId.tacho },
        { field: 'deviceID', header: this.translate.instant('deviceId'), id: VehicleFullColumnId.numberId },
        { field: 'sentGeoID', header: this.translate.instant('create-account.id'), id: VehicleFullColumnId.id },
        { field: 'networkType', header: this.translate.instant('networkType'), id: VehicleFullColumnId.networkType },
      ];
      if (this.languageService.isPolishLanguage) {
        this.cols = [
          ...this.cols,
          { field: 'etoll', header: 'e-TOLL', id: VehicleFullColumnId.etoll },
          { field: 'sent', header: 'SENT', id: VehicleFullColumnId.sent },
        ];
      }
      if (this.hasBlockerReadPermissionUser) {
        this.cols = [...this.cols, { field: 'lock', header: this.translate.instant('blocked'), id: VehicleFullColumnId.lock }];
      }
    }, TIME_OUT_FOR_LOADING_TRANSLATES);
  }

  public prepareListToDisplayWithConfigSort(data: IVehicleFullColumnSort[] = getConfigSavedListSortColumns()): void {
    this.configListSortColumns = [...data];
    this.isVisibleConfigColumnPanel = false;
  }

  private goToVehicleLocation(vehicleData: Vehicle): void {
    const coords = getVehicleCoords(vehicleData);
    this.mapService.showPointWithZoom(coords, 14);
  }

  private followVehicle(data: Vehicle): void {
    this.onChangeVehicleFollowSubsc?.unsubscribe();
    this.goToVehicleLocation(data);
  }

  public onClickVehicle(data: Vehicle): void {
    this.followVehicle(data);
  }

  private onChangeRoutePage(): void {
    this.router.events.subscribe(data => {
      if (data instanceof NavigationEnd) {
        if (data.url === '/panel/vehicle-full-list') {
          this.mapService.isVisibleVehicleList$.next(true);
          return;
        }
        this.mapService.isVisibleVehicleList$.next(false);
      }
    });
  }

  private onChangeVisibleList(): void {
    this.mapService.isVisibleVehicleList$.pipe(distinctUntilChanged()).subscribe((isVisible) => {
      if (isVisible) {
        this.showContainer();
        return;
      }
      this.hideModal();
    });
  }

  private showContainer(): void {
    this.isVisibleList = true;
    this.containerRef.nativeElement.classList.add('is-visible');
    this.containerRef.nativeElement.classList.remove('hide');
    this.getVehiclesList();
    setTimeout(() => {
      this.setPositionMapBottomControls();
    }, ANIMATION_TOGGLE_LIST_TIME);
  }

  private hideModal(): void {
    this.onChangeVehicleFollowSubsc?.unsubscribe();
    this.containerRef.nativeElement.classList.add('hide');
    this.defaultPositionMapBottomControls();
    setTimeout(() => {
      this.containerRef.nativeElement.classList.remove('is-visible');
      this.isVisibleList = false;
    }, ANIMATION_TOGGLE_LIST_TIME);
  }

  private get zoomControlsMapRef(): ChildNode {
    return document.getElementById('map-controls')?.firstChild as ChildNode;
  }

  private get mapContentRef(): Element {
    return document.getElementsByClassName('leaflet-container').item(0) as Element;
  }

  private setPositionMapBottomControls(listHeight: number = this.getContainerListHeightPx(0).number): void {
    const newMapHeight = window.innerHeight - listHeight;
    this.renderer2.setStyle(this.zoomControlsMapRef, 'height', `${ newMapHeight }px`);
    this.renderer2.setStyle(this.mapContentRef, 'height', `${ newMapHeight }px`);
    this.mapService.map?.invalidateSize();
  }

  private defaultPositionMapBottomControls(): void {
    this.renderer2.removeStyle(this.zoomControlsMapRef, 'height');
    this.renderer2.setStyle(this.mapContentRef, 'height', '100vh');
    this.mapService.map?.invalidateSize();
  }

  public onHideModal(): void {
    this.mapService.isVisibleVehicleList$.next(false);
  }

  public handleOnSelectedTags(selectedTags: Tag[]): void {
    this.form.controls['Tags'].setValue(selectedTags);
  }

  private getVehicleControlNumberByGroupId(data: Vehicle, groupId: number): number {
    let control = (data.ControlIndicators || []).filter(o => o && o.Group === groupId);
    let value = (control && control.length > 0 && control[0].LV) ? control[0].LV : null;
    return value?.V;
  }

  private sortVehicles(data: Vehicle[]): void {
    const list = [...data];

    list.sort((a, b) => {
      let result = 0;
      const aField: any = get(a, this.sortField, '');
      const bField: any = get(b, this.sortField, '');

      if (!this.sortField || this.sortField === 'vehicleName') {
        result = a.Nm!.localeCompare(b.Nm!);
      }
      if (this.sortField === 'St') {
        result = (aField as number) - (bField as number);
      }
      if (this.sortField === 'networkType') {
        result = (a.DN || '1G').localeCompare((b.DN || '1G'));
      }
      if (this.sortField === 'speed') {
        result = findVehicleSpeedParamValue(a) - findVehicleSpeedParamValue(b);
      }
      if (this.sortField === 'driver') {
        result = this.driverFullNamePipe.transform(a.LastDriver).localeCompare(this.driverFullNamePipe.transform(b.LastDriver));
      }
      if (this.sortField === 'etoll') {
        result = (a.ES as number) - (b.ES as number);
      }
      if (this.sortField === 'sent') {
        result = (a.SG as number) - (b.SG as number);
      }
      if (this.sortField === 'gps') {
        const aData = a.ControlIndicators?.find(p => p && p.Tp === 21)?.LV?.V;
        const bData = b.ControlIndicators?.find(p => p && p.Tp === 21)?.LV?.V;
        result = aData - bData;
      }
      if (this.sortField === 'gsm') {
        const aData = a.ControlIndicators?.find(p => p && p.Tp === 22)?.LV?.V;
        const bData = b.ControlIndicators?.find(p => p && p.Tp === 22)?.LV?.V;
        result = aData - bData;
      }
      if (this.sortField === 'fuel') {
        const aData = a.ControlIndicators?.find(item => item.Tp === ParameterTypeEnum.Fuel && item.LV)?.LV?.V || 0;
        const bData = b.ControlIndicators?.find(item => item.Tp === ParameterTypeEnum.Fuel && item.LV)?.LV?.V || 0;
        result = aData - bData
      }
      if (this.sortField === 'rpm') {
        const aData = this.getVehicleControlNumberByGroupId(a, 7);
        const bData = this.getVehicleControlNumberByGroupId(b, 7);
        result = aData - bData;
      }
      if (this.sortField === 'deviceID') {
        result = a.SN.localeCompare(b.SN);
      }
      if (this.sortField === 'sentGeoID') {
        result = a.SentGeoId.localeCompare(b.SentGeoId);
      }
      if (this.sortField === 'lock') {
        const aLock = isVehicleBlocked(a);
        const bLock = isVehicleBlocked(b);
        result = (!aLock && !bLock) ? (a.St as number) - (b.St as number) : (aLock === bLock) ? 0 : (aLock ? 1 : -1);
      }
      if (this.sortField === 'time') {
        const aTime = calculateVehicleTimeInCurrentState(a) || 0;
        const bTime = calculateVehicleTimeInCurrentState(b) || 0;
        result = aTime - bTime;
      }
      return this.sortOrder * result;
    });
    this.vehicles = [...list];
  }

  public setColumnSorted(event: any): void {
    this.sortField = event.field;
    this.sortOrder = event.order;
    this.sortVehicles(this.vehicles);
  }

  public onSubmitFilters(data: any, filterData: IFilterVehicle): void {
    if (filterData.type === FilterVehicleType.Status) {
      this.form.get('Status')?.setValue(data?.selectedControlValue);
      this.filtersStatusCurrentFilters = {...data};
      return;
    }
    if (filterData.type === FilterVehicleType.Group) {
      this.filtersGroupCurrentFilters = {...data};
      if (data.selectedControlValue) {
          const tagData = this.filtersGroupConfig.options.find(item => item.value === data.selectedControlValue);
          this.handleOnSelectedTags([tagData?.data]);
          return;
      }
      this.handleOnSelectedTags([]);
      return;
    }
    if (filterData.type === FilterVehicleType.Blocking) {
      this.form.get('IgnitionBlockerState')?.setValue(data?.selectedControlValue);
      this.filtersLockCurrentFilters = {...data};
      return;
    }
  }

  private onChangeFiltersFormValue(): void {
    const sub = this.form.valueChanges.pipe(
      tap(() => this.isVisibleLoader = true),
      debounceTime(300),
    ).subscribe(
      (data) => {
        let statuses: number[] = [];

        if (data.Status !== null) {
          statuses = this.filteredStatuses.find(function (f) { return f.filterType === data.Status; })?.statuses || [] as any;
        }

        const filters = {
          ...data,
          Statuses: statuses,
          IgnitionBlockerState: isNumber(data?.IgnitionBlockerState) ? data.IgnitionBlockerState === FilterLockType.Lock ? true : false : undefined,
        } as VehicleListFilterRequest;

        this.vehicleService.findVehicle(filters);
      }
    );
    this.componentSubsc.add(sub);
  }

  private resizeInit(): void {
    const mouseDown$ = fromEvent<MouseEvent>(this.resizeElement.nativeElement, 'mousedown');
    const mouseMove$ = fromEvent<MouseEvent>(document, 'mousemove');
    const mouseUp$ = fromEvent<MouseEvent>(document, 'mouseup');

    this.mouseMoveSubscription = mouseDown$.pipe(
      map(event => event.y),
      switchMap(startY => {
        this.initialY = startY;
        this.onChangeingPanelHeight = true;
        return mouseMove$.pipe(
          map(moveEvent => moveEvent.y),
          pairwise(),
          map(([previousY, currentY]) => previousY - currentY),
          filter((dy) => {
            if (dy !== 0) {
              this.isVisibleLoader = true;
            }
            return dy !== 0;
          }),
          takeUntil(mouseUp$)
        );
      }),
      debounceTime(1),
      distinctUntilChanged(),
    ).subscribe(dy => {
      const parent = this.resizeElement.nativeElement.parentElement;
      const currentHeight = parseInt(getComputedStyle(parent).height, 10);
      parent.style.height = `${currentHeight + dy}px`;
      this.setPositionMapBottomControls();
      this.onChangePanelHeight$.next();
    });
  }

  private getCurrentListWrapperHeight(): number {
    const currentHeight = parseInt(getComputedStyle(this.listWrapper.nativeElement).height, 10);
    return currentHeight;
  }

  private setListHeight(): void {
    this.scrollHeight = `${this.getCurrentListWrapperHeight() - 20}px`;
    this.setVehicleListHeightLocalStorage(this.scrollHeight);
  }

  private onChangePanelHeight(): void {
    const sub = this.onChangePanelHeight$.pipe(debounceTime(1000)).subscribe(() => {
      this.setListHeight();
      this.isVisibleLoader = false;
      this.onChangeingPanelHeight = false;
    });
    this.componentSubsc.add(sub);
  }

  private getVehiclesList(): void {
    const sub = this.vehicleService.getListFiltered$()
      .subscribe(vehicles => {
        if (this.isVisibleList) {
          this.sortVehicles(vehicles);

          setTimeout(() => {
            if (!this.onChangeingPanelHeight) {
              this.isVisibleLoader = false;
            }
          }, 1000);
        }
    });
    this.componentSubsc.add(sub);
  }

  private checkIfUserCanVisibleBlockerInfo(): void {
    this.hasBlockerReadPermissionUser = this.permissionService.hasBlockerRead() || this.permissionService.hasBlockerReadWrite();
  }

  /* filters */
  private setTranslations(): void {
    this.vehicleStatuses = this.translateHelperService.translateOptionItems(Consts.vehicleStatuses);
    this.setFiltersConfig();
  }

  private setFiltersConfig(): void {
    setTimeout(() => {
      const filtersStatusOptions: IFilterVehicleOption[] = this.vehicleStatuses.map(({ checkboxColor, label, value, data }) => ({ checkboxColor, label, value, data })) as IFilterVehicleOption[];
      this.filtersStatusConfig = {
          filterLabel: this.translate.instant('labels.status'),
          type: FilterVehicleType.Status,
          dropdownLabel: this.translate.instant('filterByStatuses'),
          options: filtersStatusOptions
      };
      this.filtersGroupConfig = {
          filterLabel: this.translate.instant('vehicle-indicator.group'),
          type: FilterVehicleType.Group,
          dropdownLabel: this.translate.instant('showOnlyGroup'),
          options: [],
      };
      this.filtersLockConfig = {
          filterLabel: this.translate.instant('blocked'),
          type: FilterVehicleType.Blocking,
          dropdownLabel: this.translate.instant('displayOnly'),
          options: [
              { label: this.translate.instant('unBlockVehicles'), value: FilterLockType.Unblock },
              { label: this.translate.instant('blockedVehicles'), value: FilterLockType.Lock }
          ],
      };
      this.getGroups();
    }, TIME_OUT_FOR_LOADING_TRANSLATES);
  }

  private getGroups(): void {
    if (!this.filtersGroupConfig?.options.length) {
        of(<TagsListFilterRequest>{Phrase: '', TagType: 0})
          .pipe(
              switchMap(request => this.webAccess.GetTagsListByFilter(request))
          )
          .subscribe(response => {
              this.filtersGroupConfig = {
                  ...this.filtersGroupConfig,
                  options: response.Result.map(item => ({ label: item.L, value: item.Id, data: item })) as IFilterVehicleOption[],
              };
          });
    }
  }

  private setVehicleListHeightLocalStorage(height: string): void {
    localStorage.setItem(LOCAL_STORAGE_VAR.VEHICLE_LIST_HEIGHT, height);
    localStorage.setItem(LOCAL_STORAGE_VAR.VEHICLE_BOTTOM_LIST_WRAPPER_HEIGHT, this.getContainerListHeightPx().px);
  }

  private getVehicleListHeightLocalStorage(): string {
    return localStorage.getItem(LOCAL_STORAGE_VAR.VEHICLE_LIST_HEIGHT) || '432px';
  }

  private setInitContainerHeight(): void {
    const height = localStorage.getItem(LOCAL_STORAGE_VAR.VEHICLE_BOTTOM_LIST_WRAPPER_HEIGHT) || '526px';
    this.renderer2.setStyle(this.containerRef.nativeElement, 'height', height);
  }

  private getContainerListHeightPx(addPxToHeight: number = 0): {px: string, number:number } {
    const currentHeight = parseInt(getComputedStyle(this.containerRef.nativeElement).height, 10);
    return {
      px: `${currentHeight + addPxToHeight}px`,
      number: currentHeight,
    };
  }

}
